import type { ReactNode } from 'react';
import React from 'react';
import {
    Grid,
    Button,
    useStyles,
    Caption,
    pxToRem,
    useTheme,
} from '@archipro-design/aria';
import * as S from './Details.mobile.style';
import {
    DownloadLine,
    EditorLink,
    PlusLight,
    SubtractLine,
} from '@archipro-design/icons';
import { displayItems, getDetailsItems } from './util/get-details-items';
import {
    DOWNLOAD_ATTR_FILE_TITLE,
    DOWNLOAD_ATTR_PROFESSIONAL_ID,
    DOWNLOAD_ATTR_PROFESSIONAL_TITLE,
    TRACK_DOWNLOAD_ATTR,
} from '@archipro-website/tracker';
import type { ProjectPageLoader } from '@modules/project/type';

const MAX_LIMITED_LINES = 3;

interface DetailsMobileProps {
    data: ProjectPageLoader['project'];
}

const DetailsMobile = ({ data }: DetailsMobileProps) => {
    const { css } = useStyles();
    const theme = useTheme();

    const [showingAll, setShowingAll] = React.useState(false);

    const detailsItems = getDetailsItems(data, {
        includeAddress: true,
        includeDownloads: false,
        includeNewFields: false,
    });

    let index = 0;

    const showItem = (
        label: string,
        value: string,
        link?: string,
        isExternal?: boolean,
    ): ReactNode => {
        if (!showingAll && ++index > MAX_LIMITED_LINES) {
            return null;
        }

        return (
            <Grid
                columns={'1fr 1fr'}
                className={css(S.ContentGrid)}
                key={label}
            >
                <Caption
                    variant="03"
                    variables={{
                        fontSize: pxToRem(14),
                        captionColor:
                            theme.siteVariables.colors.charcoal['100'],
                    }}
                >
                    {label}
                </Caption>
                <div className={css(S.ContentRightGrid)}>
                    {link ? (
                        <Button
                            className={css(S.DownloadButton)}
                            size={14}
                            as="a"
                            href={link}
                            download={isExternal ? undefined : label}
                            target={isExternal ? '_blank' : undefined}
                            iconAfter={
                                isExternal ? <EditorLink /> : <DownloadLine />
                            }
                            variant={'text'}
                            variables={{
                                buttonPadding: '0',
                                iconPadding: 0,
                            }}
                            {...{
                                [TRACK_DOWNLOAD_ATTR]: '1',
                                [DOWNLOAD_ATTR_PROFESSIONAL_ID]:
                                    data.Professional.ID,
                                [DOWNLOAD_ATTR_PROFESSIONAL_TITLE]:
                                    data.Professional.Title,
                                [DOWNLOAD_ATTR_FILE_TITLE]: value,
                            }}
                        >
                            {value}
                        </Button>
                    ) : (
                        <Caption variant="03">{value}</Caption>
                    )}
                </div>
            </Grid>
        );
    };

    return (
        <div className={css(S.Details)}>
            {displayItems(detailsItems, showItem)}
            {detailsItems.length > MAX_LIMITED_LINES - 1 && (
                <Button
                    className={css(S.ToggleButton)}
                    onClick={() => setShowingAll(!showingAll)}
                    color="primary"
                    size={14}
                    transparent
                    iconAfter={
                        showingAll ? (
                            <SubtractLine className={css(S.ShowLessIcon)} />
                        ) : (
                            <PlusLight className={css(S.ShowMoreIcon)} />
                        )
                    }
                    text
                    variables={{
                        buttonPadding: 0,
                    }}
                >
                    Show {showingAll ? 'less' : 'more'}
                </Button>
            )}
        </div>
    );
};

export default DetailsMobile;
